import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation, inject, OnDestroy } from '@angular/core';
import {maxFileUploadSizeBytes, maxFileUploadSizeMB,} from 'src/app/shared/constants';
import {Subject, takeUntil} from "rxjs";
import {DynamicFormService} from "../../form/dynamic-form/dynamic-form.service";
import {CustomValidators, isFileExtensionValid, isFileSizeValid} from "../../form/dynamic-form/dynamic-form-field/validators/validators.model";
import {Document} from '../../../models/user/document.model';
import {Menu} from 'primeng/menu';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {FileUpload} from "primeng/fileupload";
import { NgTemplateOutlet } from '@angular/common';
import { Tooltip } from 'primeng/tooltip';
import { Bind } from 'primeng/bind';
import { TableModule } from 'primeng/table';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator.component';
import { Dialog } from 'primeng/dialog';

@Component({
    selector: 'app-multiple-file-upload',
    templateUrl: './multiple-file-upload.component.html',
    styleUrls: ['./multiple-file-upload.component.css'],
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, ReactiveFormsModule, NgTemplateOutlet, Tooltip, Bind, TableModule, PrimeTemplate, StatusIndicatorComponent, Menu, Dialog]
})
export class MultipleFileUploadComponent implements OnInit, OnChanges, OnDestroy {
    private formService = inject(DynamicFormService);

    @Input() fileLimit = 10;
    @Input() maxFileSize = maxFileUploadSizeBytes;
    @Input() maxFileSizeMB = maxFileUploadSizeMB;
    @Input() acceptedFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'pdf'];
    acceptTypes = this.acceptedFileTypes.map(t => '.' + t).join(', ');
    @Input() files: Document[];
    @Input() editable = true;
    @Input() fControlName: string;
    @Input() required: boolean;
    @Input() invalid: boolean;
    @Input() fGroup: FormGroup
    @Input() fieldID: string;
    @Input() fieldName: string;

    @Output() onSelect = new EventEmitter<Document>();
    @Output() onDownload = new EventEmitter<Document>();
    @Output() onDelete = new EventEmitter<Document>();
    @Output() onChange = new EventEmitter<Document[]>();
    @ViewChild("fileUpload") fileUpload: FileUpload;

    @ViewChild('menu') menu: Menu;
    documentOptions: MenuItem[] = [];
    editingDocument: Document;
    showDocumentDelete = false;
    deletingDocument = false;
    showFileError = false;
    private selectedFile: File;
    private unsubscribe$ = new Subject<void>();

    get uploadedCount(): number {
        if (this.files?.length) {
            return this.files.filter(f => f.isUploaded).length;
        }
        return 0;
    }

    get pendingCount(): number {
        if (this.files?.length) {
            return this.files.filter(f => !f.isUploaded).length;
        }
        return 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.files && !changes.files.firstChange) {
            this.setFileIndexes();
        }
    }

    ngOnInit(): void {
        this.acceptTypes = this.acceptedFileTypes.map(t => '.' + t).join(', ');
        this.formService.clearFilesSubject$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.fileUpload.clear());

        this.setFileIndexes();

        if (this.editable) {
            if (this.fGroup) {
                const validators = [];
                if (this.required && !this.files?.length) {
                    validators.push(CustomValidators.fileRequired(null));

                    this.fGroup.controls[this.fControlName].setValidators(validators);

                    this.fGroup.controls[this.fControlName].updateValueAndValidity();
                }
            }
        }

        this.formService.loading$.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(() => {
            this.showDocumentDelete = false;
            this.deletingDocument = false;
        });
    }

    toggleDocumentOptionsMenu(document: Document, event: any): void {
        this.editingDocument = document;

        const items = [];

        if (document.isUploaded) {
            items.push({
                label: 'Download Document',
                icon: 'fa-duotone fa-file-arrow-down',
                command: () => {
                    this.downloadDocument(this.editingDocument);
                }
            });
        }

        if (this.editable) {
            items.push({
                label: document.isUploaded ? 'Delete Document' : 'Remove Document',
                icon: 'fas fa-trash-alt',
                command: () => {
                    this.showDocumentDelete = true;
                }
            });
        }

        this.documentOptions = [
            {
                label: 'Options',
                items: items
            }
        ];

        this.menu.toggle(event);
    }

    downloadDocument(document: Document): void {
        this.onDownload.emit(document);
    }

    deleteDocument(): void {
        if (this.editingDocument.isUploaded) {
            this.deletingDocument = true;
            this.onDelete.emit(this.editingDocument);
        } else {
            const idx = this.files.findIndex(d => d.idx === this.editingDocument.idx);
            this.files.splice(idx, 1);
            this.showDocumentDelete = false;
            this.onChange.emit(this.files);
        }
    }

    documentOnSelect(event: any): void {
        this.showFileError = false;

        if (this.editable) {
            let validators = [];
            if (event.target.files && event.target.files.length > 0) {
                this.selectedFile = <File>event.target.files[0];

                validators = [
                    CustomValidators.filesize(this.selectedFile, this.maxFileSize),
                    CustomValidators.fileextension(this.selectedFile, this.acceptedFileTypes)
                ];

                const f = this.selectedFile;
                if (isFileSizeValid(f, this.maxFileSize) && isFileExtensionValid(f, this.acceptedFileTypes)) {

                    const newDoc = new Document();
                    newDoc.file = f;
                    newDoc.isUploaded = false;
                    newDoc.uploadedDocumentName = f.name;

                    this.files.push(newDoc);
                    this.setFileIndexes();
                    this.onSelect.emit(newDoc);
                    this.onChange.emit(this.files);
                } else {
                    this.showFileError = true;
                }
            } else {
                this.selectedFile = undefined;
            }


            if (this.fGroup) {
                if (this.required) {
                    validators.push(CustomValidators.fileRequired(event));
                }

                this.fGroup.controls[this.fControlName].setValidators(validators);

                this.fGroup.controls[this.fControlName].updateValueAndValidity();
            }
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private setFileIndexes(): void {
        if (!this.files) {
            this.files = [];
        }

        this.files.forEach((f, i) => f.idx = i);
    }
}
